'use client';

import { ProfileTheme } from '@up2stay/up2promo-api-client';
import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { cn } from '../utils/cn';
import {
  getProfileAccentCssVars,
  resolveProfileThemePreference,
  type ProfileTokenPalette,
} from './profile-accent-color';

export type ProfileThemeMode = ProfileTheme;

type ProfileThemeRootProps = {
  accentColor?: string | null;
  theme?: ProfileThemeMode | null;
  /** Classic layout uses a warmer light palette. */
  palette?: ProfileTokenPalette;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
};

/**
 * ThemeRoot — sets handover CSS design tokens from accentColor + theme.
 */
export function ProfileThemeRoot({
  accentColor,
  theme = ProfileTheme.DARK,
  palette = 'standard',
  children,
  className,
  style,
}: ProfileThemeRootProps) {
  const [prefersDark, setPrefersDark] = useState(true);

  useEffect(() => {
    if (theme !== ProfileTheme.AUTO || typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setPrefersDark(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [theme]);

  const resolvedTheme = useMemo(
    () => resolveProfileThemePreference(theme, prefersDark),
    [theme, prefersDark],
  );

  const accentStyle = useMemo(
    () =>
      getProfileAccentCssVars(accentColor, {
        theme: resolvedTheme,
        palette,
      }),
    [accentColor, resolvedTheme, palette],
  );

  return (
    <div
      className={cn(
        // Size queries against this root so phone-frame previews get mobile
        // layout (viewport media queries would see the desktop browser width).
        '@container/profile profile-theme-root font-sans antialiased',
        // Enable Tailwind `dark:` utilities (outline buttons, inputs) inside
        // the profile tree — profile theme is independent of the page `.dark`.
        resolvedTheme === 'dark' && 'dark',
        className,
      )}
      data-profile-theme={resolvedTheme}
      data-profile-palette={palette}
      style={{ ...accentStyle, ...style }}
    >
      {children}
    </div>
  );
}

/** @deprecated Prefer ProfileThemeRoot — kept for lead-form / legacy call sites. */
export function ProfileAccentTheme({
  accentColor,
  children,
  className,
  style,
}: {
  accentColor?: string | null;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <ProfileThemeRoot
      accentColor={accentColor}
      theme={ProfileTheme.DARK}
      className={className}
      style={style}
    >
      {children}
    </ProfileThemeRoot>
  );
}
